import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Sidebar } from '@/components/layout/sidebar';
import { useCurrentUser } from '@/features/auth/use-current-user';
import { useCommandCenter } from '@/features/command-center/queries';
import { VentureTile } from '@/components/command-center/venture-tile';
import { TodayStrip } from '@/components/command-center/today-strip';
import { SystemHealthStrip } from '@/components/command-center/system-health-strip';
import { WorkflowsPanel } from '@/components/command-center/workflows-panel';

export const Route = createFileRoute('/_auth/command-center')({ component: CommandCenterPage });

/**
 * Force the Quiet Authority brand theme while the cockpit is mounted, then
 * restore whatever the user had. The rest of FLOW keeps its chosen theme.
 */
function useCockpitTheme(): void {
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-theme') ?? 'cyan';
    root.setAttribute('data-theme', 'quiet-authority');
    return () => {
      root.setAttribute('data-theme', previous);
    };
  }, []);
}

function SkeletonTile(): JSX.Element {
  return (
    <div className="h-56 animate-pulse rounded-2xl border border-border/[0.10] bg-surface-2/60" aria-hidden />
  );
}

function CommandCenterPage(): JSX.Element {
  useCockpitTheme();
  const { data: me, isLoading: meLoading } = useCurrentUser();
  const isAdmin = me?.role === 'admin';
  const { data, isLoading, isError, error, refetch } = useCommandCenter();
  const ventures = data?.ventures ?? [];
  const overviewErrors = data?.errors ?? [];

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="border-b border-border/[0.08] py-5 pl-14 pr-6 md:px-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-text-dim">SoftSync · Operating System</div>
          <h1 className="mt-1 font-display text-3xl leading-none">
            Mission <span className="italic text-text-dim">Control</span>
          </h1>
          <p className="mt-1 text-sm text-text-mute">Your whole day, across every venture.</p>
        </div>

        {meLoading ? (
          <div className="px-6 py-16 text-center text-text-mute">Loading…</div>
        ) : !isAdmin ? (
          <div className="px-6 py-16 text-center text-text-mute">This cockpit is admin-only.</div>
        ) : isError ? (
          <div className="mx-6 mt-6 rounded-2xl border border-danger/30 bg-danger/[0.06] p-6">
            <h2 className="font-display text-lg text-danger">Cockpit data didn&apos;t load</h2>
            <p className="mt-1 text-sm text-text-dim">
              {error instanceof Error ? error.message : 'The overview endpoint did not respond.'}
            </p>
            <p className="mt-1 text-xs text-text-mute">
              If the database migrations haven&apos;t been applied yet, the <code>venture_metrics</code> table won&apos;t
              exist — that&apos;s the most likely cause.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-md border border-border/[0.16] px-3 py-1.5 text-sm text-text hover:border-accent/40"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <SystemHealthStrip ventures={ventures} />
            <TodayStrip ventures={ventures} />

            {overviewErrors.length > 0 && (
              <div className="mx-6 mt-4 rounded-lg border border-warn/30 bg-warn/[0.06] px-4 py-2 text-xs text-warn">
                Partial data: {overviewErrors.map((e) => `${e.scope} (${e.message})`).join(' · ')}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 px-6 py-5 lg:grid-cols-2">
              {isLoading && ventures.length === 0
                ? Array.from({ length: 4 }, (_, i) => <SkeletonTile key={i} />)
                : ventures.map((v) => <VentureTile key={v.venture} venture={v} />)}
            </div>

            {!isLoading && ventures.length === 0 && (
              <div className="mx-6 mb-5 rounded-2xl border border-dashed border-border/[0.18] p-8 text-center text-text-mute">
                No venture snapshots yet. Run <code>tools.venture_pulse</code> (after the migrations) to populate the tiles.
              </div>
            )}

            <WorkflowsPanel />
          </>
        )}
      </main>
    </div>
  );
}
